<template>
  <v-card tile flat>
    <v-expand-x-transition>

      <!-- FILTERS -->

      <v-container style="width: 250px" class="float-right" v-show="filterDrawer">
        <v-btn
          v-on:click="newNotification"
          class="green"
          block
          large
          dark
        >
        <v-icon>mdi-bell-plus</v-icon>
          Inserisci notifica
        </v-btn>
        <v-divider class="mt-4" />
        <v-chip
          v-on:click="filterForStateOnly(0)"
          class="red mt-4 d-block text-center"
          dark
        >
          Nuove: <strong class="ml-2">{{ stateCount.NEW }}</strong>
        </v-chip>
        <v-chip
          v-on:click="filterForStateOnly(1)"
          class="orange mt-2 d-block text-center"
          dark
        >
          Assegnate: <strong class="ml-2">{{ stateCount.ASSIGNED }}</strong>
        </v-chip>
        <v-divider class="my-4" />
        <v-text-field
          v-bind:value="textFilter"
          v-on:input="applyTextFilter"
          append-icon="mdi-magnify"
          label="Filtra per richiesta"
          class="mb-4"
          clearable
          autofocus
          hide-details
        />
        <v-combobox
          v-bind:value="customerFilter"
          v-on:input="applyCustomerFilter"
          v-bind:items="customers"
          v-bind:loading="server_loading"
          v-bind:search-input.sync="customerFilterSearch"
          label="Filtra per cliente"
          class="my-4"
          clearable
          hide-details
        />
        <v-select
          v-bind:value="stateFilter"
          v-on:input="applyStateFilter"
          v-bind:items="states"
          v-bind:loading="server_loading"
          label="Filtra per stato"
          class="my-4"
          clearable
          hide-details
        />
        <v-select
          v-bind:value="periodFilter"
          v-on:input="applyPeriodFilter"
          v-bind:items="dateIntervals"
          v-bind:loading="server_loading"
          label="Filtra per periodo"
          class="my-4"
          clearable
          hide-details
        />
        <v-select
          v-bind:value="serviceFilter"
          v-on:input="applyServiceFilter"
          v-bind:items="services"
          v-bind:loading="server_loading"
          label="Filtra per servizio"
          class="my-4"
          chips
          deletable-chips
          multiple
          hide-details
        />
      </v-container>
    </v-expand-x-transition>

    <!-- LIST -->

    <v-card-text>
      <v-data-table
        v-bind:headers="headers"
        v-bind:options.sync="options"
        v-bind:items="rows"
        v-bind:server-items-length="totalRows"
        v-bind:loading="server_loading"
        v-bind:footer-props="{ 'items-per-page-options': [5, 10, 15, 20] }"
        v-on:click:row="editNotification"
        calculate-widths
      >
        <template v-slot:item.state="{ item }">
          <v-chip class="red d-block text-center" v-if="item.state === 0" dark>Nuova</v-chip>
          <v-chip class="orange d-block text-center" v-else-if="item.state === 1" dark>Assegnata  </v-chip>
          <v-chip class="green d-block text-center" v-else dark>Chiusa</v-chip>
        </template>
        <template v-slot:item.created="{ item }">
          <ellipsis-cell><span class="font-weight-medium">{{ item.created | dateTime }}</span></ellipsis-cell>
          <ellipsis-cell><span class="font-weight-light">{{ item.created_user }}</span></ellipsis-cell>
        </template>
        <template v-slot:item.updated="{ item }">
          <ellipsis-cell><span class="font-weight-medium">{{ item.updated | dateTime }}</span></ellipsis-cell>
          <ellipsis-cell><span class="font-weight-light">{{ item.updated_user }}</span></ellipsis-cell>
        </template>
        <template v-slot:item.opened="{ item }">
          <ellipsis-cell><span class="font-weight-medium">{{ item.opened | dateTime }}</span></ellipsis-cell>
          <ellipsis-cell><span class="font-weight-light">{{ item.opened_user }}</span></ellipsis-cell>
        </template>
        <template v-slot:item.closed="{ item }">
          <ellipsis-cell><span class="font-weight-medium">{{ item.closed | dateTime }}</span></ellipsis-cell>
          <ellipsis-cell><span class="font-weight-light">{{ item.closed_user }}</span></ellipsis-cell>
        </template>
        <template v-slot:item.service="{ item }">
          <ellipsis-cell><span class="font-weight-medium">{{ item.service }} - {{ item.customer }}</span></ellipsis-cell>
          <ellipsis-cell><span class="font-weight-light">{{ item.request }}</span></ellipsis-cell>
        </template>
        <template v-slot:item.request="{ item }">
          <ellipsis-cell>{{ item.request }}</ellipsis-cell>
        </template>
      </v-data-table>

      <v-btn
        v-on:click="filterDrawer = !filterDrawer"
        class="mb-3"
        absolute
        bottom
        small
      >
        {{ filterDrawer ? 'Nascondi filtri' : 'Visualizza filtri' }}
      </v-btn>
    </v-card-text>

    <!-- EDIT DIALOG -->

    <v-dialog v-model="editingNotification" fullscreen>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark v-on:click="closeNotification">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ selectedNotification.id ? 'Modifica notifica' : 'Nuova notifica' }}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn dark text v-on:click="loadLogs" v-if="user_isAdministrator && selectedNotification.id">Log</v-btn>
            <v-btn dark text v-on:click="saveNotification">Salva</v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-sheet class="mx-6 text-right" height="24">
          <span class="grey--text overline" v-if="selectedNotification.id">
            Creata {{ selectedNotification.created | dateTimeExtended }} da {{ selectedNotification.created_user }}
          </span>
        </v-sheet>

        <v-card-text class="pb-0 d-flex">
          <v-form ref="form" class="flex-grow-1" v-model="valid" lazy-validation>
            <v-row>
              <v-col class="py-0">
                <v-combobox
                  ref="selectedNotificationCustomer"
                  v-model="customerSelection"
                  v-bind:rules="[rules.required, rules.fromList]"
                  v-bind:items="customers"
                  v-bind:loading="server_loading"
                  v-bind:readonly="selectedNotificationIsReadonly"
                  v-bind:clearable="!selectedNotificationIsReadonly"
                  v-bind:hide-no-data="showingNewCustomerDialog"
                  v-bind:search-input.sync="customerSelectionSearch"
                  label="Assistito"
                  auto-select-first
                  validate-on-blur
                >
                  <template v-slot:item="{item}">
                    {{ item.text }} ({{ item.birthday | date }})
                  </template>
                  <template v-slot:no-data>
                    <v-sheet class="mx-2">
                      <v-btn v-on:click="createNewCustomer" text block>Crea un nuovo cliente</v-btn>
                    </v-sheet>
                  </template>
                </v-combobox>
              </v-col>
              <v-col class="py-0">
                <v-combobox
                  v-model="serviceSelection"
                  v-bind:rules="[rules.required, rules.fromList]"
                  v-bind:items="allServices"
                  v-bind:loading="server_loading"
                  v-bind:readonly="selectedNotificationIsReadonly"
                  v-bind:clearable="!selectedNotificationIsReadonly"
                  label="Servizio"
                  auto-select-first
                  validate-on-blur
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col class="pt-0">
                <v-chip
                  class="mr-1"
                  color="blue-grey lighten-5"
                  v-for="(contact, index) in selectedNotificationContacts"
                  v-bind:key="index"
                  v-bind:href="`${contact.href}:${contact.value}`"
                >
                  <v-icon small>{{ contact.icon }}</v-icon>
                  <span class="ml-1">{{ contact.value }}</span>
                </v-chip>
              </v-col>
            </v-row>

            <v-textarea
              v-model="selectedNotification.request"
              v-bind:rules="[rules.required]"
              v-bind:readonly="selectedNotificationIsReadonly"
              label="Richiesta"
              auto-grow
              validate-on-blur
            />
          </v-form>

          <div class="ml-6 my-3">


            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  v-on:click="selectedNotification.readonly = !selectedNotification.readonly"
                  v-show="selectedNotification.id > 0"
                  height="100%"
                >
                  <v-icon>{{ selectedNotification.readonly ? 'mdi-lock' : 'mdi-lock-open-variant' }}</v-icon>
                </v-btn>
              </template>
              <span>{{ selectedNotification.readonly ? 'Abilita le modifiche ai dati della richiesta' : 'Disabilita le modifiche ai dati della richiesta' }}</span>
            </v-tooltip>
          </div>
        </v-card-text>

        <!-- Response -->
        <v-card-text v-if="selectedNotificationIsAssigned || selectedNotificationHasResponse">
          <span class="caption">Risposta</span>

          <div
            v-for="(response, index) in selectedNotification.responseList.filter(response => 'state' in response === false)"
            v-bind:key="index"
          >
            <v-sheet color="blue-grey lighten-5 pa-2">
              <p class="text-pre-wrap ma-0">{{ response.text }}</p>
            </v-sheet>
            <div class="text-right blue-grey--text text--lighten-2 overline">
              Inserita {{ response.when | dateTimeExtended }} da {{ response.user }}
            </div>
          </div>
        </v-card-text>

        <v-card-text v-if="selectedNotificationIsAssigned">
          <v-row dense>
            <v-col class="py-0">
              <v-btn v-on:click="responseDialog = true" class="primary" block>Aggiungi risposta</v-btn>
            </v-col>
            <v-col class="py-0">
              <v-btn v-on:click="changeNotificationState('closed')" class="green" block dark>Chiudi la notifica</v-btn>
            </v-col>
            <v-col class="py-0">
              <v-btn v-on:click="changeNotificationState('unassigned')" class="red" block dark>Riapri la notifica</v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-text v-if="selectedNotificationIsUnassigned">
          <v-btn v-on:click="changeNotificationState('assigned')" class="orange" block dark>Prendi in carico</v-btn>
        </v-card-text>

        <v-card-text v-if="selectedNotificationIsClosed">
          <v-btn v-on:click="changeNotificationState('unassigned')" class="red" block dark>Riapri la notifica</v-btn>
        </v-card-text>

        <!-- State history -->
        <v-card-text v-if="selectedNotificationHasResponse">
          <v-timeline dense>
            <v-timeline-item
              v-for="(response, index) in selectedNotification.responseList.filter(response => 'state' in response)"
              v-bind:key="index"
              v-bind:color="response.state === 0 ? 'red' : response.state === 1 ? 'orange' : 'green'"
              fill-dot
              right
              small
            >
              {{ response.text }} {{ response.when | dateTimeExtendedLT }} da {{ response.user }}
            </v-timeline-item>
            <v-timeline-item
              color="red"
              fill-dot
              right
              small
            >
              Notifica creata {{ selectedNotification.created | dateTimeExtended }} da {{ selectedNotification.created_user }}
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- NEW CUSTOMER DIALOG -->

    <customer-details v-model="newCustomer" />

    <!-- LOG DIALOG -->

    <v-dialog v-model="logDialog" width="500" v-if="selectedNotification.id && user_isAdministrator">
      <v-card>
        <v-card-title>Log associato alla notifica</v-card-title>
        <v-card-text>
          <v-sheet height="400" class="overflow-y-auto">
            <v-sheet v-for="(event, index) in logEvents" v-bind:key="index">
              {{ event.created | dateTime }} - {{ event.user }} - {{ event.message }}
            </v-sheet>
          </v-sheet>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="primary" v-on:click="logDialog = false">Chiudi</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- NEW RESPONSE DIALOG -->

    <v-dialog v-model="responseDialog">
      <v-card>
        <v-card-title>Aggiungi risposta</v-card-title>
        <v-card-text>
          <v-textarea
            ref="selectedResponseText"
            v-model="selectedResponse"
            v-bind:rules="[rules.required]"
            auto-grow
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="primary" v-on:click="saveResponse" v-bind:loading="server_loading">Salva</v-btn>
          <v-btn class="primary" v-on:click="closeResponse">Annulla</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import moment from 'moment'
import server from '../mixins/server'
import user from '../mixins/user'
import utils from '../mixins/utils'
import contacts from '../mixins/contacts'
import period from '../mixins/period'
import EllipsisCell from '../components/EllipsisCell'
import CustomerDetails from '../components/CustomerDetails'

export default {
  name: 'Notifications',

  mixins: [ server, utils, user, contacts, period ],

  components: { EllipsisCell, CustomerDetails },

  data: () => ({
    headers: [
      { text: 'Stato', value: 'state', width: '13%' },
      { text: 'Servizio e cliente', value: 'service' },
      { text: 'Inserita', value: 'created', width: '13%' },
      { text: 'Viste', value: 'read_count', width: '1%', align: 'right', sortable: false },
      { text: 'Assegnata', value: 'opened', width: '13%' },
      { text: 'Chiusa', value: 'closed', width: '13%' },
      { text: 'Ultimo agg.', value: 'updated', width: '13%' },
    ],
    filterDrawer: true,
    textFilter: '',
    serviceFilter: [],
    customerFilter: null,
    stateFilter: null,
    periodFilter: null,
    totalRows: 0,
    rows: [],
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['created'],
      sortDesc: [true],
      groupBy: [],
      groupDesc: [],
      mustSort: false,
      multiSort: false,
    },
    rules: {
      required: value => !!value || 'Valore richiesto.',
      fromList: value => typeof value === 'object' || 'Il valore deve essere selezionato dalla lista',
    },
    valid: false,
    selectedNotification: {},
    selectedResponse: '',
    responseDialog: false,
    allServices: [],
    services: [],
    serviceSelection: null,
    customers: [],
    customerSelection: null,
    customerSelectionSearch: '',
    customerFilterSearch: '',
    states: [
      { text: 'Nuova', value: 0 },
      { text: 'Assegnata', value: 1 },
      { text: 'Chiusa', value: 2 }
    ],
    dateIntervals: [],
    newCustomer: {},
    stateCount: {},
    updateInterval: null,
    logDialog: false,
    logEvents: [],
  }),

  computed: {
    editingNotification: {
      get: function() { return 'id' in this.selectedNotification; },
      set: function(value) { if (!value) this.selectedNotification = {}; },
    },
    selectedNotificationIsReadonly: function() { return this.server_loading || this.selectedNotification.readonly; },
    selectedNotificationIsUnassigned: function() { return this.selectedNotification.id && !this.selectedNotification.opened_user_id && !this.selectedNotification.closed_user_id; },
    selectedNotificationIsAssigned: function() { return this.selectedNotification.id && this.selectedNotification.opened_user_id && !this.selectedNotification.closed_user_id; },
    selectedNotificationIsClosed: function() { return this.selectedNotification.id && this.selectedNotification.opened_user_id && this.selectedNotification.closed_user_id; },
    selectedNotificationContacts: function() { return this.customerSelection ? this.contacts_load(this.customerSelection.contacts) : ''; },
    selectedNotificationHasResponse: function() { return this.selectedNotification.responseList && this.selectedNotification.responseList.length > 0; },
    showingNewCustomerDialog: function() { return 'id' in this.newCustomer; },
  },

  watch: {
    options: {
      handler () {
        // Intervals are set at mount, this prevent loading
        // before mount
        this.dateIntervals.length && this.loadRows();
      },
      deep: true,
    },
    responseDialog: {
      handler (newValue) { newValue && this.utils_focusInput('selectedResponseText', 200); }
    },
    newCustomer: {
      handler (newValue) {
        if ('newId' in newValue) {
          // Load the newly created customer
          this.server_query({
            url: 'Customer/index/' + newValue.newId,
            onSuccess: (data) => {
              this.customers = [{
                value: data.id,
                text: data.name,
                contacts: data.contacts,
              }];

              this.selectedNotification.customer_id = data.id;
              this.customerSelection = this.customers[0];
              this.$refs.form.resetValidation();
            },
          });
        }
      },
    },
    customerSelectionSearch: {
      handler (newValue) { this.utils_delayExecute(() => { this.loadCustomers(newValue); }, 500); }
    },
    customerFilterSearch: {
      handler (newValue) { this.utils_delayExecute(() => { this.loadCustomers(newValue); }, 500); }
    },
  },

  mounted: function() {
    this.loadServices();
    this.dateIntervals = this.period_list;

    if (this.$route.meta.filter) {
      this.serviceFilter = [this.$route.params.serviceId];
      this.periodFilter = this.$route.params.period;
    } else {
      this.serviceFilter = this.user_services;
      this.periodFilter = '30d';
    }

    this.loadRows();
    this.updateInterval = setInterval(this.loadRows, 60 * 1000);
  },

  destroyed: function() { this.updateInterval && clearInterval(this.updateInterval); },

  methods: {
    applyTextFilter: function(value) { this.textFilter = value; this.utils_delayExecute(this.applyFilter, 300); },
    applyServiceFilter: function(value) { this.serviceFilter = value; this.applyFilter(); },
    applyCustomerFilter: function(value) { this.customerFilter = value; this.applyFilter(); },
    applyStateFilter: function(value) { this.stateFilter = value; this.applyFilter(); },
    applyPeriodFilter: function(value) { this.periodFilter = value; this.applyFilter(); },

    applyFilter: function() {
      // When appling the filter we move to first page and this
      // forces the reload of the rows
      if (this.options.page != 1)
        this.options.page = 1;
      else
        this.loadRows();
    },

    filterForStateOnly: function(state) {
      this.textFilter = '';
      this.serviceFilter = [];
      this.customerFilter = null;
      this.periodFilter = null;
      this.stateFilter = state;
      this.applyFilter();
    },

    loadRows: function() {
      const parameters = {};

      parameters['page'] = this.options.page;
      parameters['rows'] = this.options.itemsPerPage;

      // Add filters
      this.textFilter && (parameters['filter'] = this.textFilter);
      this.serviceFilter.length && (parameters['service_id'] = this.serviceFilter);
      this.customerFilter && (parameters['customer_id'] = this.customerFilter.value);
      this.stateFilter != null && (parameters['state'] = this.stateFilter);
      this.periodFilter && ([parameters['from'], parameters['to']] = this.period_toDateInterval(this.periodFilter));

      // Add sorting
      if (this.options.sortBy.length > 0) {
        parameters['order'] = this.options.sortBy[0];
        if (this.options.sortDesc[0]) {
          parameters['order'] = '-' + parameters['order'];
        }
      }

      this.server_query({
        url: 'Notification/list',
        parameters: parameters,
        onError: () => {
          this.rows = [];
          this.totalRows = 0;
        },
        onSuccess: (data) => {
          this.rows = data.rows;
          this.totalRows = data.total;
        },
      });

      // Update state count
      this.server_query({
        url: 'Notification/countNewAndOpened',
        onSuccess: (data) => { this.stateCount = data; }
      })
    },

    loadServices: function() {
      this.server_query({
        url: 'Service/list/active/true/order/description',
        onSuccess: (data) => {
          const services = [];

          data.rows.forEach(service => {
            if (service.active || service.id === this.selectedNotification.service_id) {
              service = {
                value: service.id,
                text: service.description,
              };
              services.push(service);
            }
          });

          this.services = services;
        },
      });

      this.server_query({
        url: 'Service/list/active/true/order/description/all/true',
        onSuccess: (data) => {
          this.allServices = data.rows.map(service => ({
            value: service.id,
            text: service.description,
          }));
        },
      });
    },

    loadCustomers: function(filter) {
      if (filter)
        this.server_query({
          url: 'Customer/search',
          parameters: {
            rows: 20,
            active: true,
            filter: filter
          },
          onSuccess: (data) => {
            const customers = [];

            data.forEach(customer => {
              if (customer.active || customer.id === this.selectedNotification.customer_id) {
                customer = {
                  value: customer.id,
                  text: customer.name,
                  contacts: customer.contacts,
                  birthday: customer.birthday,
                };
                customers.push(customer);
              }
            });

            this.customers = customers;
          },
        });
      else
        this.customers = [];
    },

    loadLogs: function() {
      this.server_query({
        url: 'Notification/log/' + this.selectedNotification.id,
        onSuccess: (data) => {
          this.logEvents = data;
          this.logDialog = true;
        }
      });
    },

    newNotification: function() {
      this.loadServices();

      // Create an empty notification
      this.selectedNotification = {
        id: 0,
        active: '1',
        readonly: false,
        responseList: [],
      };
      this.customerSelection = null;
      this.serviceSelection = null;

      // Move focus on the customer
      this.utils_focusInput('selectedNotificationCustomer', 200);
    },

    editNotification: function(item) {
      // Load notification
      this.server_query({
        url: 'Notification/index/' + item.id,
        onSuccess: (data) => {
          data.readonly = true;
          data.responseList = (data.response) ? JSON.parse(data.response) : [];
          this.selectedNotification = data;

          this.customerSelection = {
            value: data.customer_id,
            text: data.customer,
            contacts: data.contacts,
          }
          this.customers = [ this.customerSelection ];

          this.serviceSelection = {
            value: data.service_id,
            text: data.service,
          };
        },
      });
    },

    closeNotification: function() {
      this.$refs.form.resetValidation();
      this.selectedNotification = {};
      this.customerSelection = null;
      this.serviceSelection = null;
      this.loadRows();
    },

    saveNotification: function() {
      // Validate form first
      if (this.$refs.form.validate()) {
        const isNew = !this.selectedNotification.id;
        const data = {};

        data['customer_id'] = this.customerSelection.value;
        data['service_id'] = this.serviceSelection.value;
        data['request'] = this.selectedNotification.request;
        data['response'] = this.selectedNotification.responseList ? JSON.stringify(this.selectedNotification.responseList) : '';

        ['opened_user_id', 'opened', 'closed_user_id', 'closed'].forEach((key) => {
          if (key in this.selectedNotification)
            data[key] = this.selectedNotification[key];
        })

        const parameters = {
          url: `Notification/index/${this.selectedNotification.id || 0}`,
          data: data,
          onSuccess: this.closeNotification
        };

        if (isNew) {
          this.server_save(parameters);
        } else {
          this.server_update(parameters);
        }
      }
    },

    changeNotificationState: function(state) {
      switch (state) {
        case 'unassigned':
          this.selectedNotification.opened = null;
          this.selectedNotification.opened_user_id = null;
          this.selectedNotification.closed = null;
          this.selectedNotification.closed_user_id = null;
          this.addStateChange(0, 'Notifica riaperta');
          break;
        case 'assigned':
          this.selectedNotification.opened = null;
          this.selectedNotification.opened_user_id = this.user_id;
          this.selectedNotification.closed = null;
          this.selectedNotification.closed_user_id = null;
          this.addStateChange(1, 'Notifica presa in carico');
          break;
        case 'closed':
          this.selectedNotification.closed = null;
          this.selectedNotification.closed_user_id = this.user_id;
          this.addStateChange(2, 'Notifica chiusa');
          break;
      }
    },

    addStateChange: function(state, message) {
      this.selectedNotification.responseList.unshift({
        when: moment().format('YYYY-MM-DD HH:mm'),
        user: this.user_name,
        state: state,
        text: message
      });
    },

    addResponse: function(response) {
      this.selectedNotification.responseList.unshift({
        when: moment().format('YYYY-MM-DD HH:mm'),
        user: this.user_name,
        text: response
      });
    },

    saveResponse: function() {
      if (this.selectedResponse) {
        this.addResponse(this.selectedResponse);
        this.closeResponse()
      }
    },

    closeResponse: function() {
      this.selectedResponse = null;
      this.responseDialog = false;
    },

    createNewCustomer: function() { this.newCustomer = { id: 0, active: '1', name: this.customerSelectionSearch }; }
  }
}
</script>